import { AccountOwnership, UserType } from 'app/types/User';
import { UserStatus } from 'app/types/UserStatus';
import { BASE_URL } from "../../core/apiClient";
import { constantToDisplayName } from "../../core/constantToDisplayName";
import { AccessType, RequireAuth, UserRole, useProductsQuery } from 'legoland-sdk/dist/experimental';
import { useState } from 'react';
import styled from 'styled-components';
import { Box, Button, Checkbox, Input, Label, tombac } from 'tombac';
import { ExportIcon, FiltersIcon, SearchIcon } from 'tombac-icons';
import { createUsersLinkFromQuery } from "./userApi";
import { accountOwnershipToDisplayName } from "./accountOwnershipToDisplayName";
import { userTypeToDisplayName } from "./userTypeToDisplayName";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FilterGroupLabel = styled(Label).attrs(() => ({
  as: 'div'
})).withConfig({
  displayName: "UsersFilter__FilterGroupLabel"
})(["display:block;margin-bottom:", ";"], tombac.space(1));
const defaultCheckboxGroupFilter = [];
function CheckboxGroupFilter({
  filter = defaultCheckboxGroupFilter,
  options,
  onChange
}) {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: options.map(option => {
      return /*#__PURE__*/_jsx(Checkbox, {
        label: option.label,
        checked: filter.indexOf(option.value) > -1,
        onChange: event => {
          if (event.target.checked) {
            onChange([...filter, option.value]);
          } else {
            onChange(filter.filter(r => r !== option.value));
          }
        }
      }, option.value);
    })
  });
}
const UsersFilter = ({
  query,
  onChange,
  role
}) => {
  const {
    filters
  } = query;
  const products = useProductsQuery().data?.products;
  const [showFilters, setShowFilters] = useState(true);
  return /*#__PURE__*/_jsxs("div", {
    "data-sentry-component": "UsersFilter",
    "data-sentry-source-file": "UsersFilter.tsx",
    children: [/*#__PURE__*/_jsxs(Box, {
      $width: "100%",
      $display: "flex",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "UsersFilter.tsx",
      "data-sentry-element": "Box",
      children: [/*#__PURE__*/_jsx(Input, {
        $maxWidth: "70sp",
        $background: "#fff",
        $flex: "1",
        onChange: e => onChange({
          ...filters,
          searchValues: e.target.value
        }),
        placeholder: "Search user",
        prepend: /*#__PURE__*/_jsx(SearchIcon, {}),
        type: "text",
        value: filters.searchValues ?? '',
        "data-sentry-element": "Input",
        "data-sentry-source-file": "UsersFilter.tsx",
        "data-sentry-element": "Input"
      }), /*#__PURE__*/_jsxs(Box, {
        $ml: "auto",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "UsersFilter.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(RequireAuth, {
          role: [UserRole.Admin, UserRole.Distributor],
          "data-sentry-element": "RequireAuth",
          "data-sentry-source-file": "UsersFilter.tsx",
          "data-sentry-element": "RequireAuth",
          children: /*#__PURE__*/_jsx(Button, {
            as: "a",
            href: BASE_URL + createUsersLinkFromQuery(query, 'xls'),
            prepend: /*#__PURE__*/_jsx(ExportIcon, {}),
            size: "s",
            variant: "flat",
            "data-sentry-element": "Button",
            "data-sentry-source-file": "UsersFilter.tsx",
            "data-sentry-element": "Button",
            children: "Export"
          })
        }), /*#__PURE__*/_jsx(Button, {
          prepend: /*#__PURE__*/_jsx(FiltersIcon, {}),
          onClick: () => setShowFilters(b => !b),
          variant: "secondary",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "UsersFilter.tsx",
          "data-sentry-element": "Button",
          children: showFilters ? 'Hide filters' : 'Show filters'
        })]
      })]
    }), showFilters && /*#__PURE__*/_jsxs(UsersFilters, {
      children: [/*#__PURE__*/_jsxs(UsersFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Status"
        }), /*#__PURE__*/_jsx(UsersFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.statuses,
            options: Object.entries(UserStatus).map(o => ({
              label: o[0],
              value: o[1]
            })),
            onChange: statuses => onChange({
              ...filters,
              statuses
            })
          })
        })]
      }), role !== UserRole.Distributor && /*#__PURE__*/_jsxs(UsersFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Roles"
        }), /*#__PURE__*/_jsx(UsersFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.roles,
            options: Object.entries(UserRole).map(o => ({
              label: o[0],
              value: o[1]
            })),
            onChange: roles => onChange({
              ...filters,
              roles
            })
          })
        })]
      }), products && /*#__PURE__*/_jsxs(UsersFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Products"
        }), /*#__PURE__*/_jsx(UsersFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.products,
            options: products.map(o => ({
              label: o.displayName,
              value: o.id
            })).filter(product => role === UserRole.Distributor ? product.value !== 'rer' : true),
            onChange: products => onChange({
              ...filters,
              products
            })
          })
        })]
      }), role !== UserRole.Distributor && /*#__PURE__*/_jsxs(UsersFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Account ownership"
        }), /*#__PURE__*/_jsx(UsersFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.accountOwnerships,
            options: Object.entries(AccountOwnership).map(o => ({
              label: accountOwnershipToDisplayName(o[1]),
              value: o[1]
            })),
            onChange: accountOwnerships => {
              onChange({
                ...filters,
                accountOwnerships
              });
            }
          })
        })]
      }), role !== UserRole.Distributor && /*#__PURE__*/_jsxs(UsersFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Type"
        }), /*#__PURE__*/_jsx(UsersFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.types,
            options: Object.entries(UserType).map(o => ({
              label: userTypeToDisplayName(o[1]),
              value: o[1]
            })),
            onChange: types => {
              onChange({
                ...filters,
                types
              });
            }
          })
        })]
      }), /*#__PURE__*/_jsxs(UsersFiltersGroup, {
        children: [/*#__PURE__*/_jsx(FilterGroupLabel, {
          children: "Access type"
        }), /*#__PURE__*/_jsx(UsersFiltersGroupInputs, {
          children: /*#__PURE__*/_jsx(CheckboxGroupFilter, {
            filter: filters.productAccessTypes,
            options: Object.entries(AccessType).filter(([, value]) => {
              if (role === UserRole.Distributor) {
                return value !== AccessType.Demo && value !== AccessType.Internal && value !== AccessType.CustomAgreement;
              } else return value;
            }).map(o => ({
              label: constantToDisplayName(o[1]),
              value: o[1]
            })),
            onChange: productAccessTypes => onChange({
              ...filters,
              productAccessTypes
            })
          })
        })]
      })]
    })]
  });
};
const UsersFilters = styled.div.withConfig({
  displayName: "UsersFilter__UsersFilters"
})(["display:flex;flex-wrap:wrap;"]);
const UsersFiltersGroup = styled.div.withConfig({
  displayName: "UsersFilter__UsersFiltersGroup"
})(["margin:", ";padding:", ";border-right:", ";margin-bottom:", ";"], tombac.space(3, 0), tombac.space(0, 3), tombac.parse('1u solid --neutral-300'), tombac.space(3));
const UsersFiltersGroupInputs = styled.div.withConfig({
  displayName: "UsersFilter__UsersFiltersGroupInputs"
})(["display:grid;grid-auto-flow:column;grid-gap:0.8rem;grid-template-rows:1fr 1fr 1fr 1fr;"]);
export default UsersFilter;